import React from "react"

import classNames from "classnames"

import { ProductCategoryObj } from "scrivito/Objs/ProductCategory/ProductCategoryObjClass"
import { ScrivitoLink } from "scrivito/scrivito-link/ScrivitoLink"
import { isEmpty } from "shared/lib/arrayUtils"
import { useEvent } from "shared/lib/useEvent"
import { useRovingTabIndexGrid } from "shared/lib/useRovingTabIndexGrid"
import { PORTFOLIO_COLUMNS_BY_BREAKPOINT } from "shared/types/Breakpoints"
import { Button, ButtonProps } from "shared/ui/buttons/Button"

import { ProductCategoryCard } from "entities/product-categories/ui/product-category-card/ProductCatgeoryCard"

import "./styles/_product-category-grid.scss"

interface ProductCategoryGridProps {
  categories: ProductCategoryObj[] | null | undefined
  mode: "link" | "button"
  onSelect?: (category: ProductCategoryObj) => void
  isLoading?: boolean
  emptyMessage?: string
}

export const ProductCategoryGrid = React.memo(
  ({ categories, mode, emptyMessage, onSelect }: ProductCategoryGridProps) => {
    const CardComponent =
      mode === "link" ? ProductCategoryGridLink : ProductCategoryGridButton

    const { containerProps, getElementProps } = useRovingTabIndexGrid<
      HTMLElement,
      HTMLUListElement
    >({
      disableRovingTabIndex: true,
      elementCount: categories?.length ?? 0,
      isRoundRobin: false,
      columnsByBreakpoint: PORTFOLIO_COLUMNS_BY_BREAKPOINT
    })

    if (isEmpty(categories)) {
      return (
        <div className="product-category-grid">
          {emptyMessage ? (
            <div className="product-category-grid__empty-message">
              {emptyMessage}
            </div>
          ) : null}
        </div>
      )
    }

    return (
      <ul className="product-category-grid" {...containerProps}>
        {categories?.map((category, index) => (
          <CardComponent
            key={category.id()}
            item={category}
            onSelect={onSelect}
            {...getElementProps(index)}
          />
        ))}
      </ul>
    )
  }
)

const ProductCategoryGridLink = React.forwardRef<
  HTMLElement,
  { item: ProductCategoryObj } & Omit<
    React.HTMLProps<HTMLAnchorElement>,
    "ref" | "onSelect"
  >
>(({ item, className, ...props }, ref) => {
  return (
    <ScrivitoLink
      to={item}
      {...props}
      ref={ref as React.Ref<HTMLAnchorElement>}
      className={classNames("anchor", className)}
    >
      <ProductCategoryCard productCategory={item} />
    </ScrivitoLink>
  )
})

ProductCategoryGridLink.displayName = "ProductCategoryGridLink"

const ProductCategoryGridButton = React.forwardRef<
  HTMLElement,
  {
    item: ProductCategoryObj
    onSelect?: (category: ProductCategoryObj) => void
  } & Omit<ButtonProps, "ref" | "onClick" | "onSelect">
>(({ item, onSelect, ...props }, ref) => {
  const handleSelect = useEvent(() => {
    onSelect?.(item)
  })

  return (
    <Button
      onClick={handleSelect}
      {...props}
      ref={ref as React.Ref<HTMLButtonElement>}
    >
      <ProductCategoryCard productCategory={item} />
    </Button>
  )
})

ProductCategoryGridButton.displayName = "ProductCategoryGridButton"
